import { useEffect, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card'
import { Label } from '@/components/ui/label'

/**
 * TaxConsentGate — IRC §7216 tax consent modal.
 *
 * Shown before any tax return upload flow. Once consented for an engagement,
 * this gate is hidden on subsequent logins for that engagement.
 *
 * Props:
 *   engagementId (string) — the engagement context for this consent
 *   children (React.ReactNode) — content to render once consent is confirmed
 */
export function TaxConsentGate({
  engagementId,
  children,
}: {
  engagementId: string
  children: React.ReactNode
}) {
  const { user } = useAuth()
  const [aiProviderChecked, setAiProviderChecked] = useState(false)
  const [authorityChecked, setAuthorityChecked] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showGate, setShowGate] = useState(true)

  useEffect(() => {
    if (!engagementId) return
    apiClient.get(`/v1/consent/tax-7216?engagement_id=${engagementId}`)
      .then(r => { if (r.data?.consent_exists) setShowGate(false) })
      .catch(() => {})
  }, [engagementId])

  if (!user || !showGate) {
    return <>{children}</>
  }

  const canContinue = aiProviderChecked && authorityChecked && !submitting

  const handleContinue = async () => {
    if (!canContinue) return
    setSubmitting(true)
    setError(null)
    try {
      await apiClient.post('/v1/consent/tax-7216', {
        engagement_id: engagementId,
        ai_provider: 'Anthropic PBC',
      })
      setShowGate(false)
    } catch {
      setError('Something went wrong recording your consent. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" role="presentation">
      <Card
        className="w-full max-w-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tax-consent-title"
        aria-describedby="tax-consent-description"
      >
        <CardHeader className="space-y-3 border-b">
          <CardTitle id="tax-consent-title" className="text-2xl">
            Tax Return Processing Consent Required
          </CardTitle>
          <CardDescription id="tax-consent-description" className="text-base">
            IRC §7216 tax return privacy compliance
          </CardDescription>
        </CardHeader>
        <CardContent className="max-h-96 overflow-y-auto space-y-6 py-6">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm">
            <p className="text-amber-900 leading-relaxed">
              Your tax return data will be processed by artificial intelligence as part of our due diligence analysis. The AI provider is <strong>Anthropic PBC</strong>.
            </p>
          </div>

          <div className="space-y-4">
            <Label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-accent transition-colors">
              <input
                type="checkbox"
                className="mt-1 size-4 shrink-0 rounded border-input cursor-pointer"
                checked={aiProviderChecked}
                onChange={e => setAiProviderChecked(e.target.checked)}
                aria-label="I understand my tax return data will be processed by Anthropic PBC"
              />
              <span className="font-normal text-sm">
                I understand my tax return data will be processed by Anthropic PBC for AI-assisted analysis.
              </span>
            </Label>

            <Label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-accent transition-colors">
              <input
                type="checkbox"
                className="mt-1 size-4 shrink-0 rounded border-input cursor-pointer"
                checked={authorityChecked}
                onChange={e => setAuthorityChecked(e.target.checked)}
                aria-label="I have authority to authorize this disclosure of tax return information"
              />
              <span className="font-normal text-sm">
                I have authority to authorize this disclosure of tax return information as required by IRC §7216.
              </span>
            </Label>
          </div>

          {error && (
            <p className="text-sm text-destructive bg-destructive/10 p-3 rounded">
              {error}
            </p>
          )}
        </CardContent>
        <CardFooter className="border-t justify-end gap-3 pt-6">
          <Button
            variant="outline"
            disabled={submitting}
            onClick={() => window.history.back()}
          >
            Back
          </Button>
          <Button
            disabled={!canContinue}
            onClick={handleContinue}
            className="min-w-32"
          >
            {submitting ? 'Saving…' : 'I Consent'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}
